// src/sim/lighting/fov.ts — rot-js PreciseShadowcasting wrapper + cone post-filter (VIS-01, VIS-02, VIS-09).
//
// Invariants:
//   - Deterministic: rot-js's lightPasses callback reads grid (pure); ROT.RNG is seeded ONCE
//     per round via initLighting() before any compute() call (Pitfall 2).
//   - Zero allocation: writes into caller-provided outBitset; never allocates per tick.
//   - Pitfall 3 sign convention: sim facing is CCW y-UP; tile-space dy is y-DOWN.
//     Compute tile-space angle as Math.atan2(-dy, dx) so it matches sim facing. The unit test
//     at __tests__/fov.test.ts Test 6 LOCKS this convention.
//   - rot-js is permitted only here (ESLint allowlist scoped to src/sim/lighting/** — P2 D-14).
//
// P3 (03-05-PLAN Task 3): cone logic factored into `computeConeFov` so both the
// torch (P2) and the narrower peek cone (P3 D-24) can share the same underlying
// rot-js shadowcasting + cone post-filter without duplicating the Pitfall 3
// sign-convention handling. `computeTorchFov` is now a thin delegate that
// supplies the torch constants.
//
// Source: .planning/phases/02-fov-lighting/02-RESEARCH.md §Pattern 1.

import * as ROT from 'rot-js';
import type { WalkabilityGrid } from '../../shared/types/map';
import { isWalkable } from '../map/grid';
import { clearBitset, setBit } from './bitset';
import { P2_TORCH_HALF_ANGLE_RAD, P2_TORCH_RADIUS_TILES } from './constants';

/**
 * Generic cone FOV: rot-js precise shadowcasting + half-angle cone post-filter.
 * The centre tile is always set (same as torch); all other visible tiles are
 * included iff they fall within `±halfAngleRad` of `facingRad` (sim convention,
 * y-UP / CCW).
 *
 * Pitfall 3: tile-space dy is y-DOWN, so the tile-space angle is computed as
 * `atan2(-dy, dx)` to match sim facing. Any consumer that needs a different
 * cone (peek, dog nose, future NPC vision) calls this directly with its own
 * halfAngleRad + radiusTiles.
 */
export function computeConeFov(
  cx: number,
  cy: number,
  facingRad: number,
  halfAngleRad: number,
  radiusTiles: number,
  grid: WalkabilityGrid,
  widthInTiles: number,
  outBitset: Uint32Array,
): void {
  clearBitset(outBitset);
  const originX = Math.floor(cx);
  const originY = Math.floor(cy);

  const fov = new ROT.FOV.PreciseShadowcasting(
    (x, y) => isWalkable(grid, x, y),
    { topology: 8 },
  );

  fov.compute(originX, originY, radiusTiles, (x, y, _r, visibility) => {
    if (visibility <= 0) return;
    const dx = x - originX;
    const dy = y - originY;
    if (dx === 0 && dy === 0) {
      setBit(outBitset, y * widthInTiles + x);
      return;
    }
    // Pitfall 3: negate dy to convert tile-space (y-DOWN) to sim facing (y-UP).
    const ang = Math.atan2(-dy, dx);
    let delta = ang - facingRad;
    while (delta > Math.PI) delta -= 2 * Math.PI;
    while (delta <= -Math.PI) delta += 2 * Math.PI;
    if (Math.abs(delta) <= halfAngleRad) {
      setBit(outBitset, y * widthInTiles + x);
    }
  });
}

/**
 * Torch FOV: P2 D-06 cone (60° total, 8-tile radius). Thin wrapper over
 * {@link computeConeFov} that supplies the torch tunables. Kept as a named
 * export because render-side torch code + the golden-hash regression test
 * reference it directly.
 */
export function computeTorchFov(
  px: number,
  py: number,
  facingRad: number,
  grid: WalkabilityGrid,
  widthInTiles: number,
  outBitset: Uint32Array,
): void {
  computeConeFov(
    px,
    py,
    facingRad,
    P2_TORCH_HALF_ANGLE_RAD,
    P2_TORCH_RADIUS_TILES,
    grid,
    widthInTiles,
    outBitset,
  );
}
